import { Link } from 'react-router-dom'
import { useQueries } from '@tanstack/react-query'
import { api, type Target } from '@/api/client'
import { ConfirmDialog } from '@/ui'
import { useAccountUsage } from './shared'

/** Where an account is used, and how many posts are waiting there, asked before
 *  the account is switched off. A stopped account cannot post, so every
 *  waiting post of these places fails when its time comes. */
export function DisableAccountDialog({
  t,
  open,
  onOpenChange,
  onConfirm,
}: {
  t: Pick<Target, 'id' | 'name'>
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void | Promise<void>
}) {
  const used = useAccountUsage().byId(t.id)

  const campaignCounts = useQueries({
    queries: used.campaigns.map((c) => {
      const platforms = Object.entries(c.PlatformConfigs ?? {})
        .filter(([, p]) => p.enabled !== false && p.target_id === t.id)
        .map(([k]) => k)
      return {
        queryKey: ['accountDisableUse', 'campaign', c.ID, platforms.join(',')],
        enabled: open,
        queryFn: async () => {
          let n = 0
          for (const platform of platforms) {
            n += (await api.draftCounts({ campaignId: c.ID, platform })).counts.scheduled ?? 0
          }
          return n
        },
      }
    }),
  })
  const appCounts = useQueries({
    queries: used.apps.map((a) => ({
      queryKey: ['accountDisableUse', 'app', a.id],
      enabled: open,
      queryFn: async () => (await api.redeemPosts(a.id, 'scheduled')).counts.scheduled ?? 0,
    })),
  })

  const count = (q: { data?: number; isLoading: boolean }) =>
    q.isLoading ? '세는 중' : `예약 글 ${q.data ?? 0}개`
  const nothing = used.campaigns.length + used.apps.length === 0

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${t.name} 계정을 중지할까`}
      description="중지한 계정으로는 글도 DM도 나가지 않고 연결 테스트도 되지 않는다. 아래에서 쓰는 곳의 예약 글은 때가 되면 실패한다."
      body={
        nothing ? (
          <p className="text-table text-muted-foreground">이 계정을 쓰는 캠페인과 리딤 앱이 없다.</p>
        ) : (
          <ul className="space-y-1 text-table" aria-label="이 계정을 쓰는 곳">
            {used.campaigns.map((c, i) => (
              <li key={`c${c.ID}`} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  캠페인{' '}
                  <Link to={`/campaigns/${c.ID}`} className="font-medium hover:underline">
                    {c.Name}
                  </Link>
                </span>
                <span className="shrink-0 text-muted-foreground">{count(campaignCounts[i])}</span>
              </li>
            ))}
            {used.apps.map((a, i) => (
              <li key={`a${a.id}`} className="flex justify-between gap-3">
                <span className="min-w-0 truncate">
                  리딤 앱{' '}
                  <Link to={`/redeem/${a.id}`} className="font-medium hover:underline">
                    {a.name}
                  </Link>
                </span>
                <span className="shrink-0 text-muted-foreground">{count(appCounts[i])}</span>
              </li>
            ))}
          </ul>
        )
      }
      confirmLabel="중지"
      confirmVariant="destructive"
      onConfirm={onConfirm}
    />
  )
}
